import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { useSelector } from 'react-redux';
import featureEnabled from 'shared/helper/feature';
import { T, useTranslate } from 'shared/helper/translate';
import Link from 'shared/components/Link';
import { getChinaLocalization } from 'shared/account/reducer';
import Popover from 'researcher/components/Popover';
import { getEditingStatus, getTrialFingerprintFlagged } from '../reducer';
import { formatForLocale } from '../helpers/date';
import { recordUpgradeNowClick, recordTryForFreeClick } from '../../researcher/helpers/gtm';
export var RubriqEditingStatusTooltip = function RubriqEditingStatusTooltip() {
  var _useSelector = useSelector(function (state) {
      return state;
    }),
    curie = _useSelector.curie;
  var _getEditingStatus = getEditingStatus({
      curie: curie
    }),
    hasTrial = _getEditingStatus.hasTrial,
    hasExpiredTrial = _getEditingStatus.hasExpiredTrial,
    trialExpirationDate = _getEditingStatus.trialExpirationDate,
    hasPremium = _getEditingStatus.hasPremium,
    hasExpiredPremium = _getEditingStatus.hasExpiredPremium,
    premiumExpirationDate = _getEditingStatus.premiumExpirationDate,
    hasPreferredGroup = _getEditingStatus.hasPreferredGroup,
    hasExpiredPreferredGroup = _getEditingStatus.hasExpiredPreferredGroup,
    preferredGroupExpirationDate = _getEditingStatus.preferredGroupExpirationDate,
    inTrialGracePeriodBlocked = _getEditingStatus.inTrialGracePeriodBlocked,
    isPremiumTrial = _getEditingStatus.isPremiumTrial;
  var _useTranslate = useTranslate(),
    t = _useTranslate.t;
  var getStatusTooltip = function getStatusTooltip() {
    /* 
        NOTE: For now this is just using a set order of relevance. 
        Targeted support for multi-plan-related tooltips will be added in WRT-746. 
    */

    if (hasTrial) {
      if (hasExpiredTrial) {
        return 'curie.trial-expired-editing-tooltip';
      }
      if (!hasPremium) {
        return 'curie.trial-access-editing-tooltip';
      }
      return 'curie.trial-full-access-editing-tooltip';
    }
    if (hasPremium) {
      // Trial past-due: payment failed during grace period — treat same as expired
      if (inTrialGracePeriodBlocked) {
        return 'curie.premium-expired-editing-tooltip-no-date';
      }
      if (hasExpiredPremium) {
        return premiumExpirationDate ? 'curie.premium-expired-editing-tooltip' : 'curie.premium-expired-editing-tooltip-no-date';
      }
      if (isPremiumTrial) {
        return 'curie.trial-full-access-editing-tooltip';
      }
      return 'curie.premium-access-editing-tooltip';
    }
    if (hasPreferredGroup) {
      return hasExpiredPreferredGroup ? 'curie.preferred-group-expired-editing-tooltip' : 'curie.preferred-group-access-editing-tooltip';
    }

    // no plan
    return 'curie.no-access-editing-tooltip';
  };
  var getStatusTooltipParams = function getStatusTooltipParams() {
    if (hasTrial) {
      return formatForLocale(trialExpirationDate, t);
    }
    if (hasExpiredPremium) {
      return premiumExpirationDate ? formatForLocale(premiumExpirationDate, t) : null;
    }
    if (hasExpiredPreferredGroup) {
      return formatForLocale(preferredGroupExpirationDate, t);
    }
    return null;
  };
  return /*#__PURE__*/React.createElement(Popover, {
    content: getStatusTooltip(),
    params: getStatusTooltipParams(),
    "data-bs-toggle": "tooltip",
    "data-bs-placement": "bottom"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: "fa-light fa-circle-info"
  }));
};
var RubriqEditingStatus = function RubriqEditingStatus(_ref) {
  var _ref$isNoEditsMessage = _ref.isNoEditsMessage,
    isNoEditsMessage = _ref$isNoEditsMessage === void 0 ? false : _ref$isNoEditsMessage;
  var _useSelector2 = useSelector(function (state) {
      return state;
    }),
    curie = _useSelector2.curie;
  var _getEditingStatus2 = getEditingStatus({
      curie: curie
    }),
    noPlan = _getEditingStatus2.noPlan,
    hasTrial = _getEditingStatus2.hasTrial,
    hasExpiredTrial = _getEditingStatus2.hasExpiredTrial,
    trialDaysRemaining = _getEditingStatus2.trialDaysRemaining,
    hasPremium = _getEditingStatus2.hasPremium,
    hasActivePremium = _getEditingStatus2.hasActivePremium,
    hasPreferredGroup = _getEditingStatus2.hasPreferredGroup,
    hasExpiredPreferredGroup = _getEditingStatus2.hasExpiredPreferredGroup,
    inTrialGracePeriodBlocked = _getEditingStatus2.inTrialGracePeriodBlocked;

  // A premium plan that is not active (cancelled, paid past-due, expired) has lost
  // access and should be treated the same as an expired plan.
  var hasInaccessiblePremium = hasPremium && !hasActivePremium;
  var hasCheckIcon = hasTrial && !hasExpiredTrial || hasPremium && hasActivePremium && !inTrialGracePeriodBlocked || hasPreferredGroup && !hasExpiredPreferredGroup;
  var hasLink = noPlan || hasTrial || hasInaccessiblePremium || hasExpiredPreferredGroup;
  var isChinaUser = useSelector(getChinaLocalization);
  var trialFingerprintFlagged = useSelector(getTrialFingerprintFlagged);
  var paddleBillingForChina = featureEnabled('rubriq_china_also_uses_paddle');
  var useCartInsteadOfPaddle = isChinaUser && !paddleBillingForChina;
  var isChinaFreeTrialFeatureFlagEnabled = !featureEnabled('curie_group_based_china_trials_enabled');
  var getStatusText = function getStatusText() {
    /* 
        NOTE: For now this is just using a set order of relevance. 
        Targeted support for multi-plan-related status text will be added in WRT-746. 
    */

    if (hasTrial && !hasPremium) {
      return hasExpiredTrial ? 'free trial ended' : '%s days left in free trial';
    }
    if (hasPremium) {
      if (inTrialGracePeriodBlocked) {
        return 'premium required';
      }
      return hasInaccessiblePremium ? 'premium plan expired' : 'full access';
    }
    if (hasPreferredGroup) {
      return hasExpiredPreferredGroup ? 'preferred group plan expired' : 'full access';
    }

    // no access
    return 'premium required';
  };
  var getStatusTextParams = function getStatusTextParams() {
    if (hasTrial && !hasExpiredTrial) {
      return trialDaysRemaining;
    }
    return null;
  };
  var getLinkProps = function getLinkProps() {
    if (useCartInsteadOfPaddle) {
      return {
        to: '/rubriq/plan',
        source: 'try for free'
      };
    }
    if (noPlan && trialFingerprintFlagged) {
      return {
        to: '/rubriq/plan/subscribe',
        source: 'upgrade-now'
      };
    }
    return noPlan ? {
      to: '/rubriq/plan',
      source: 'try for free'
    } : {
      to: '/rubriq/plan/subscribe/premium',
      source: 'upgrade-now'
    };
  };
  var linkProps = getLinkProps();
  var handleUpgradeClick = function handleUpgradeClick() {
    var _document$getElementB;
    var id = linkProps.source === 'upgrade-now' ? 'upgrade_now_free_trial_btn' : 'upgrade_try_for_free_trial_btn';
    var buttonText = (_document$getElementB = document.getElementById(id)) === null || _document$getElementB === void 0 ? void 0 : _document$getElementB.innerText;
    if (linkProps.source === 'upgrade-now') {
      recordUpgradeNowClick({
        buttonText: buttonText
      });
    } else if (linkProps.source === 'try for free') {
      recordTryForFreeClick({
        buttonText: buttonText
      });
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "d-flex flex-column gap-2 flex-md-row gap-md-3 ".concat(isNoEditsMessage ? 'align-items-center' : 'align-items-start align-items-md-center')
  }, /*#__PURE__*/React.createElement("div", {
    className: "d-flex align-items-center gap-2"
  }, hasCheckIcon && /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: "fa-solid fa-check"
  }), /*#__PURE__*/React.createElement(T, {
    source: getStatusText(),
    params: getStatusTextParams()
  }), /*#__PURE__*/React.createElement(RubriqEditingStatusTooltip, null)), hasLink && !hasPremium && /*#__PURE__*/React.createElement(Link, {
    id: linkProps.source === 'upgrade-now' ? 'upgrade_now_free_trial_btn' : 'upgrade_try_for_free_trial_btn',
    className: hasCheckIcon && !isNoEditsMessage ? 'ms-2 ms-md-0 rubriq-status-link' : '',
    to: linkProps.to,
    onClick: handleUpgradeClick
  }, /*#__PURE__*/React.createElement(T, {
    source: linkProps.source
  })));
};
export default RubriqEditingStatus;